/**
 * 图片上传工具函数
 * 提供将图片上传到 Shopify CDN 的功能
 */

import axios from "axios";
import FormData from "form-data";
import { readFile } from "fs/promises";
import { get } from "lodash";
import { randomUUID } from "crypto";

// Shopify API configuration
const SHOPIFY_TOKEN =
  process.env.SHOPIFY_TOKEN || "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";
const SHOPIFY_URL =
  process.env.SHOPIFY_URL ||
  "https://easyemailpro-cdn.myshopify.com/admin/api/2024-01/graphql.json";

const headers = {
  "Content-Type": "application/json",
  "X-Shopify-Access-Token": SHOPIFY_TOKEN,
};

// Helper function to upload image to S3 via Shopify staged upload
async function uploadImageToS3(params: {
  file: Buffer;
  parameters: Array<{ name: string; value: string }>;
  url: string;
}): Promise<void> {
  const formData = new FormData();

  params.parameters.forEach((item) => {
    formData.append(item.name, item.value);
  });
  formData.append("file", params.file, {
    contentType: "image/png", // You might want to detect this from the file
  });

  await axios.post(params.url, formData, {
    headers: {
      ...formData.getHeaders(),
      "X-Shopify-Access-Token": SHOPIFY_TOKEN,
    },
  });
}

// Helper function to upload to Shopify
async function uploadToShopify(url: string): Promise<string> {
  const data = JSON.stringify({
    query: `mutation fileCreate($files: [FileCreateInput!]!) {
      fileCreate(files: $files) {
        files {
          fileStatus
          alt
          createdAt
          ... on GenericFile {
            id
            url
            mimeType
            originalFileSize
          }
        }
        userErrors {
          field
          message
        }
      }
    }`,
    variables: {
      files: {
        contentType: "FILE",
        originalSource: url,
      },
    },
  });

  const { data: fileData } = await axios.post(SHOPIFY_URL, data, {
    headers: headers,
  });
  const fileGid = fileData?.data?.fileCreate?.files[0]?.id;

  if (!fileGid) {
    const errors = fileData?.data?.fileCreate?.userErrors;
    throw new Error(
      `Failed to upload to Shopify: ${JSON.stringify(errors || "Unknown error")}`
    );
  }

  return fileGid;
}

// Helper function to get generic file URL
async function getGenericFileUrl(fileGid: string): Promise<string> {
  const queryData = JSON.stringify({
    query: `query {
      node(id: "${fileGid}") {
        id
        ... on GenericFile {
          id
          url
          mimeType
          originalFileSize
        }
      }
    }`,
    variables: {},
  });
  const { data: genericFileData } = await axios.post(SHOPIFY_URL, queryData, {
    headers: headers,
  });
  const url = genericFileData?.data?.node?.url;

  if (url) {
    return url.replace(/\?v=.*$/, "");
  }
  await new Promise((resolve) => setTimeout(resolve, 100));
  return getGenericFileUrl(fileGid);
}

// Main upload function
export async function uploadImage(
  fileBuffer: Buffer,
  fileName: string,
  mimeType: string
): Promise<string> {
  if (!SHOPIFY_TOKEN || !SHOPIFY_URL) {
    throw new Error(
      "SHOPIFY_TOKEN and SHOPIFY_URL environment variables are required"
    );
  }

  const postData = JSON.stringify({
    query: `mutation stagedUploadsCreate($input: [StagedUploadInput!]!) {
      stagedUploadsCreate(input: $input) {
        stagedTargets {
          resourceUrl
          url
          parameters {
            name
            value
          }
        }
        userErrors {
          field
          message
        }
      }
    }`,
    variables: {
      input: [
        {
          filename:
            randomUUID().replace(/-/g, "").toLowerCase() + "_" + fileName,
          mimeType: mimeType,
          resource: "IMAGE",
          httpMethod: "POST",
        },
      ],
    },
  });

  const { data } = await axios.post(SHOPIFY_URL, postData, {
    headers: headers,
  });

  const error = get(data, "data.stagedUploadsCreate.userErrors");

  if (Array.isArray(error) && error.length > 0) {
    throw new Error(`Shopify API error: ${JSON.stringify(error)}`);
  }

  const payload = get(data, "data.stagedUploadsCreate.stagedTargets.0");

  if (!payload) {
    throw new Error("Failed to get staged upload target from Shopify");
  }

  try {
    await uploadImageToS3({
      parameters: payload.parameters,
      url: payload.url,
      file: fileBuffer,
    });

    const gid = await uploadToShopify(payload.resourceUrl);
    const finalUrl = await getGenericFileUrl(gid);

    return finalUrl;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Upload failed: ${errorMessage}`);
  }
}

// Helper function to download image from URL
export async function downloadImageFromUrl(url: string): Promise<{
  buffer: Buffer;
  mimeType: string;
  fileName: string;
}> {
  try {
    const response = await axios.get(url, {
      responseType: "arraybuffer",
    });

    const buffer = Buffer.from(response.data);
    const contentType = response.headers["content-type"] || "image/png";
    const urlPath = new URL(url).pathname;
    const fileName = urlPath.split("/").pop() || `image_${Date.now()}.png`;

    return {
      buffer,
      mimeType: contentType,
      fileName,
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to download image from URL: ${errorMessage}`);
  }
}

// Helper function to read local image file
export async function readLocalImage(filePath: string): Promise<{
  buffer: Buffer;
  mimeType: string;
  fileName: string;
}> {
  try {
    const buffer = await readFile(filePath);
    const pathParts = filePath.split("/");
    const fileName = pathParts[pathParts.length - 1];

    // Detect MIME type from file extension
    const ext = fileName.split(".").pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
    };
    const mimeType = mimeTypes[ext || ""] || "image/png";

    return {
      buffer,
      mimeType,
      fileName,
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to read local image file: ${errorMessage}`);
  }
}
